import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { toastWithUndo } from "@/components/ui/ToastSurface";
import { useCarriedItem, useTarget } from "@/components/patterns/SortableList";
import { EmptyState } from "@/components/ui/EmptyState";
import { Menu, MenuGroup, MenuItem } from "@/components/ui/Menu";
import { SortableLanes, type Lanes } from "@/components/ui/SortableLanes";
import type { ElementRow } from "@/lib/element-registry";
import { MOST_MEMBERS, type UndoneDrop } from "@/lib/figure-registry";
import type { NestOutcome } from "@/hooks/figure-nesting-entry-points";

/**
 * Nesting one component under another in a figure's component table, by drag or by menu.
 *
 * Dropping a component on another makes them lettered members of one numeral; dropping it on
 * the zone under the table takes it out of its sub-category. A group row is neither carried nor
 * a target: a group inside a group is a set the API refuses, and a component joins a group by
 * a drop on one of its members. The menu does what each drop does, for anyone not dragging.
 */

/** The kind of item a component row carries. */
const COMPONENT = "component";

/** The drop zone's id, which is no element's. */
const TAKE_OUT = "take-out-of-sub-category";

/** Nothing here sorts: every drag lands on a target. */
const NO_LANES: Lanes = {};

const keepAsIs = () => undefined;

/** The drag the table's rows take part in, and what each landing asks for. */
export function NestingDrag({
  name,
  onNest,
  onTakeOut,
  children,
}: {
  /** How a reader hears a component: its numeral and title. */
  name: (elementId: string) => string;
  onNest: (dragged: string, target: string) => void;
  onTakeOut: (elementId: string) => void;
  children: ReactNode;
}) {
  return (
    <SortableLanes
      served={NO_LANES}
      name={(id) =>
        id === TAKE_OUT ? "the sub-category drop zone" : name(String(id))
      }
      onArrange={keepAsIs}
      lands={[COMPONENT]}
      onLand={({ source, target }) => {
        if (target === TAKE_OUT) onTakeOut(source);
        else onNest(source, target);
      }}
    >
      {() => children}
    </SortableLanes>
  );
}

/** A component row that can be carried onto another and can take one dropped on it. */
export function useNestable({
  elementId,
  label,
}: {
  elementId: string;
  /** The handle's name: "Make 20 hilt a sub-category of another component". */
  label: string;
}) {
  const carried = useCarriedItem({
    id: elementId,
    type: COMPONENT,
    label,
    size: "sm",
  });
  const target = useTarget({ id: elementId, accept: COMPONENT });
  return {
    ref: (element: Element | null) => {
      carried.ref(element);
      target.ref(element);
    },
    handle: (
      <span title="Drag onto another component to make it a sub-category">
        {carried.handle}
      </span>
    ),
    chrome: `${carried.chrome} ${target.chrome}`,
  };
}

/** Where a member is dropped to take it out of its sub-category. */
export function TakeOutZone() {
  const target = useTarget({ id: TAKE_OUT, accept: COMPONENT });
  return (
    <EmptyState
      ref={target.ref}
      shape="row"
      className={`m-[14px] py-3 text-[11.5px] ${target.chrome}`}
    >
      Drop a component here to take it out of its sub-category
    </EmptyState>
  );
}

/** A component the menu offers to join, named as the table shows it. */
export interface JoinOffer {
  elementId: string;
  label: string;
}

/** The row's options: join another component, and, for a member, leave its sub-category. */
export function NestMenu({
  numeral,
  member,
  others,
  onJoin,
  onTakeOut,
}: {
  numeral: string;
  member: boolean;
  others: JoinOffer[];
  onJoin: (target: string) => void;
  onTakeOut: () => void;
}) {
  const [joining, setJoining] = useState(false);
  return (
    <Menu name={`Options for component ${numeral}`}>
      <MenuItem
        title="Make a sub-category of…"
        keepOpen
        onSelect={() => {
          setJoining(true);
        }}
      />
      {joining && (
        <MenuGroup heading="Join">
          {others.map((offer) => (
            <MenuItem
              key={offer.elementId}
              title={offer.label}
              onSelect={() => {
                setJoining(false);
                onJoin(offer.elementId);
              }}
            />
          ))}
        </MenuGroup>
      )}
      {member && (
        <MenuItem title="Take out of sub-category" onSelect={onTakeOut} />
      )}
    </Menu>
  );
}

/**
 * Say what a nesting edit did that the table alone does not: a drop refused as full, and a
 * group that dissolved, which the toast's Undo puts back.
 *
 * A dissolved group is named by the numeral it showed, read from the numerals as they stood
 * before the edit, because after it the group has no numeral to read.
 */
export function announceNesting(
  outcome: NestOutcome,
  numeralsBefore: ReadonlyMap<string, string>,
  restore: (
    group: ElementRow,
    memberIds: string[],
    undoing?: UndoneDrop,
  ) => void,
): void {
  if (outcome.kind === "full") {
    toast.error(
      `A sub-category holds at most ${String(MOST_MEMBERS)} components.`,
    );
    return;
  }
  if (outcome.kind === "unchanged") return;
  for (const { group, memberIds, undoing } of outcome.dissolved) {
    const numeral = numeralsBefore.get(group.element_id) ?? "";
    toastWithUndo(`Sub-category ${numeral} dissolved.`, () => {
      restore(group, memberIds, undoing);
    });
  }
}

/** What the table's nesting acts do on a figure set: the edit, then what it tells the attorney. */
export function nestingActs(
  set: {
    nestComponent: (dragged: string, target: string) => NestOutcome;
    takeOutComponent: (elementId: string) => NestOutcome;
    restoreGroup: (
      group: ElementRow,
      memberIds: string[],
      undoing?: UndoneDrop,
    ) => void;
  },
  numerals: ReadonlyMap<string, string>,
) {
  return {
    onNest: (dragged: string, target: string) => {
      announceNesting(
        set.nestComponent(dragged, target),
        numerals,
        set.restoreGroup,
      );
    },
    onTakeOut: (elementId: string) => {
      announceNesting(
        set.takeOutComponent(elementId),
        numerals,
        set.restoreGroup,
      );
    },
  };
}
